/**
 * This wraps the router and handles:
 * 1. session management
 * 2. page transitions
 * 3. messsage wipe on route change
 */
import { getReplay } from '@sentry/react';
import { Location } from 'history';
import { kebabCase } from 'lodash/fp';
import { FC, Suspense, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ImpersonationResumeModal from 'dashboard/components/Application/ImpersonationResumeModal';
import Notifications from 'dashboard/components/Application/Notifications';
import OrganizationPlanStatusBanner from 'dashboard/components/Application/OrganizationPlanStatusBanner';
import SegmentIframe from 'dashboard/components/Application/SegmentIframe';
import SetupMfaBanner from 'dashboard/components/Application/SetupMfaBanner';
import CssReset from 'dashboard/components/CssReset';
import ErrorState from 'dashboard/components/ErrorState';
import GlobalStyles from 'dashboard/components/GlobalStyles';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import Router from 'dashboard/components/Router';
import SupportChat from 'dashboard/components/SupportChat';
import WithNavbar from 'dashboard/components/WithNavbar';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { saveOverrideFeatureFlags } from 'dashboard/redux/actions/feature-flags';
import { clearMessagesOnRouteChange } from 'dashboard/redux/actions/messages';
import { isPersonerd } from 'dashboard/redux/selectors/current-user';
import { RootState } from 'dashboard/redux/store';
import { expireSession, refreshSession } from 'dashboard/redux/thunk-actions/current-user-session';
import EventsClient from 'lib/clients/EventsClient';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { FeatureFlag, FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { setLocationForReporting } from 'lib/reporting';

// Automatically logout of impersonation after 30 minutes inactivity
const IMPERSONATION_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

ReactModal.setAppElement('#react-root');

interface LocationWithQuery extends Location {
  query: Record<string, string>;
}

type ApplicationProps = ApplicationPropsFromRedux & RouteComponentProps<{}>;

const FLAG_OVERRIDE_PREFIX = 'flag_override_';

const toFeatureFlagOverride = (query: Record<string, string>) => {
  if (query == null) {
    return {};
  }
  return Object.entries(query).reduce((previousValue, currentValue) => {
    if (!currentValue[0].startsWith(FLAG_OVERRIDE_PREFIX)) {
      return previousValue;
    }
    previousValue[currentValue[0].slice(FLAG_OVERRIDE_PREFIX.length) as FeatureFlag] =
      currentValue[1] === 'true';
    return previousValue;
  }, {} as FeatureFlagsMap);
};

const getElementName = (element: HTMLElement) => {
  return element.id || (element.getAttribute && element.getAttribute('data-react-class'));
};

const ApplicationView: FC<ApplicationProps> = (props) => {
  const navigate = useNavigate();
  const {
    history,
    clearMessagesOnRouteChange,
    saveOverrideFeatureFlags,
    impersonationMode,
    environment,
  } = props;

  /**
   * We track when authn related token refresh happens, because if we load the app before this, we will hit an
   * AuthenticatedRoute which sees we have no tokens and bounces us to the login page.
   */
  const [sessionTokensReady, setSessionTokensReady] = useState<boolean>(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState<boolean>(false);

  /**
   * Last time the user interacted with the page, used to determine if we should show the impersonation modal.
   *
   * Uses a ref to avoid excessively re-rendering the component when the user interacts with the page.
   */
  const activityAt = useRef<number>(new Date().getTime());

  const pathname = props.history.location?.pathname;

  useEffect(() => {
    const handleRouteChange = (location: LocationWithQuery, action: string) => {
      if (action !== 'REPLACE') {
        clearMessagesOnRouteChange();
      }

      setLocationForReporting(location.pathname);
      SegmentClient.sendSegmentPageEvent(location.pathname, {
        environmentType: environment?.type,
        impersonationMode: impersonationMode,
      });

      const featureFlagsMap = toFeatureFlagOverride(location.query);
      saveOverrideFeatureFlags(featureFlagsMap);
    };

    const unregister = history.listen(handleRouteChange);

    return () => {
      unregister();
    };
  }, [
    history,
    clearMessagesOnRouteChange,
    saveOverrideFeatureFlags,
    impersonationMode,
    environment,
  ]);

  useEffect(() => {
    const initializeSession = async () => {
      if (SessionClientDashboard.refreshToken) {
        try {
          await props.refreshSession(SessionClientDashboard.refreshToken);
        } catch (e) {
          navigate('/dashboard/login');
        }
      }
      setSessionTokensReady(true);
    };

    initializeSession();
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      const currentTime = new Date().getTime();
      if (
        props.impersonationMode &&
        currentTime >= activityAt.current + IMPERSONATION_INACTIVITY_TIMEOUT_MS
      ) {
        setShowImpersonationModal(true);
      } else {
        activityAt.current = currentTime;
      }
    };

    window.addEventListener('focus', handleActivity);
    window.addEventListener('mousemove', handleActivity);

    return () => {
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
    };
  }, [props.impersonationMode]);

  const handleClickTrackingRef = useRef<(event: MouseEvent) => void>();

  useEffect(() => {
    handleClickTrackingRef.current = (event: MouseEvent) => {
      let element = event.target as HTMLElement;
      while (!getElementName(element) && element.parentElement) {
        element = element.parentElement;
      }

      const elementId = getElementName(element);

      EventsClient.logAmplitude('dashboard-ui.click', {
        element: elementId,
        path: pathname,
      });

      SegmentClient.sendSegmentTrackEvent('click', {
        elementId: elementId,
        environmentType: props.environment?.type,
        impersonationMode: props.impersonationMode,
      });
    };
  }, [props.environment, props.impersonationMode, pathname]);

  useEffect(() => {
    const handleClickTracking = (event: MouseEvent) => {
      if (handleClickTrackingRef.current) {
        handleClickTrackingRef.current(event);
      }
    };

    window.addEventListener('click', handleClickTracking, { capture: true });

    return () => {
      window.removeEventListener('click', handleClickTracking);
    };
  }, []);

  useEffect(() => {
    SegmentClient.setUserId(props.currentUser?.id);

    if (props.isPersonerd) {
      try {
        getReplay()?.startBuffering();
      } catch {
        // Do nothing if this errors
      }
    }
  }, [props.currentUser, props.isPersonerd]);

  const { featureFlags } = props.featureFlags;

  if (!sessionTokensReady) {
    return <LoadingIndicator fullscreen />;
  } else {
    return (
      <StyledAppContainer
        id='app'
        data-test='app'
        data-marketing-id={kebabCase(props.environment?.attributes.name)}
      >
        <CssReset />
        <ModalWrapper isOpen={showImpersonationModal}>
          <ImpersonationResumeModal
            organization={props.organization}
            endSession={() => {
              setShowImpersonationModal(false);
              props.expireSession();
              navigate('/dashboard/login');
            }}
            resumeSession={() => {
              activityAt.current = new Date().getTime();
              setShowImpersonationModal(false);
            }}
          />
        </ModalWrapper>

        <GlobalStyles />
        <Notifications />
        <WithNavbar
          isLoggedIn={props.isLoggedIn}
          environments={props.environments}
          organization={props.organization}
        >
          <SegmentIframe />
          <OrganizationPlanStatusBanner isLoggedIn={props.isLoggedIn} />
          <SetupMfaBanner isLoggedIn={props.isLoggedIn} />
          <Suspense fallback={null}>
            <PersonaErrorBoundary fallback={ErrorState}>
              <Router />
            </PersonaErrorBoundary>
          </Suspense>
        </WithNavbar>
        <SupportChat
          pageContentEnabled={featureFlags['dashboard:enable-support-chat-page-content'] ?? false}
          isLoggedIn={props.isLoggedIn}
          organization={props.organization}
        />
      </StyledAppContainer>
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    environment: state.currentEnvironment.currentEnvironment,
    environments: state.organization.environments,
    organization: state.organization.organization,
    impersonationMode: state.currentUser.impersonationMode,
    currentUser: state.currentUser.currentUser,
    // isLoggedIn() depends solely on JWT tokens, so can be true before
    // organization, etc. are done loading. This avoids errors due to
    // organization being null.
    // TODO: [PER-4626] some of these checks can be replaced by clearing
    // the Redux store on logout.
    isLoggedIn:
      state.currentUser.currentUser != null &&
      state.organization.organization != null &&
      SessionClientDashboard.isLoggedIn(),
    featureFlags: state.featureFlags,
    isPersonerd: isPersonerd(state),
  };
};

const mapDispatchToProps = {
  clearMessagesOnRouteChange,
  expireSession,
  refreshSession,
  saveOverrideFeatureFlags,
};

const applicationConnector = connect(mapStateToProps, mapDispatchToProps);
type ApplicationPropsFromRedux = ConnectedProps<typeof applicationConnector>;

export default withRouter(applicationConnector(ApplicationView));

const StyledAppContainer = styled.div`
  height: 100%;
`;
